class CurrencyModal extends HTMLElement {
  constructor() {
    super()
    this.modal = this.querySelector('.currency-modal')
    this.modalContentFront = this.querySelector('.front-modal')
    this.modalContentBack = this.querySelector('.back-modal')
    this.closeSpanX = this.querySelector('.currency-modal__close-btn')
    this.closeModalBtn = this.querySelector('.currency-modal-overlay')
    this.previousFocusedElement = null
    this.activeTrigger = null
    this.dialog = this.querySelector('.currency-modal-content')
    this.handleFocusTrap = this.handleFocusTrap.bind(this)
    this.handleFocusContainment = this.handleFocusContainment.bind(this)
    this.handleModalClick = this.handleModalClick.bind(this)
    this.load()
    this.updateSelectedLocale()
  }

  load() {
    this.addListeners()
    this.modal.addEventListener('keydown', this.handleFocusTrap)
    document.addEventListener('focusin', this.handleFocusContainment)
  }

  isOpen() {
    return this.modal.style.display === 'flex'
  }

  getDialogFocusableElements() {
    if (!this.dialog) {
      return []
    }

    return this.dialog.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  }

  getFocusableElements() {
    const dialogElements = Array.from(this.getDialogFocusableElements())

    if (this.closeModalBtn?.tabIndex >= 0) {
      return [...dialogElements, this.closeModalBtn]
    }

    return dialogElements
  }

  updateSelectedLocale() {
    const localeSelector = this.querySelector('[name=locale_code]')
    if (localeSelector) {
      localeSelector.value = window.Shopify.locale
    }
  }

  addListeners() {
    this.closeSpanX.addEventListener('click', () => this.closeModal())
    this.closeModalBtn.addEventListener('click', () => this.closeModal())
    this.closeModalBtn.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.closeModal()
      }
    })

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.style.display === 'flex') {
        this.closeModal()
      }
    })

    this.modal.addEventListener('click', this.handleModalClick)
  }

  handleModalClick(event) {
    if (event.target === this.modal) {
      this.closeModal()
    }
  }

  lockScroll() {
    document.documentElement.classList.add('overflow-hidden')
    document.body.classList.add('overflow-hidden')
  }

  unlockScroll() {
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')
  }

  setTriggersExpanded(isExpanded) {
    document.querySelectorAll('.header-country-selector').forEach((trigger) => {
      trigger.setAttribute(
        'aria-expanded',
        isExpanded && trigger === this.activeTrigger ? 'true' : 'false'
      )
    })
  }

  showModal(trigger = null, options = {}) {
    this.openedFromMobile = Boolean(options.openedFromMobile)
    this.previousFocusedElement = document.activeElement
    this.activeTrigger =
      trigger?.classList?.contains('header-country-selector') ? trigger : null
    this.modalContentFront.style.display = 'none'
    this.modal.style.display = 'flex'
    this.modalContentBack.style.display = 'block'
    this.setTriggersExpanded(true)
    this.lockScroll()
    this.trapFocus()
  }

  isFocusRestoreTarget(element) {
    if (!element?.isConnected) {
      return false
    }

    return !element.closest('[aria-hidden="true"]')
  }

  getFocusRestoreTarget() {
    if (this.openedFromMobile) {
      return document.querySelector('.menu-mobile-button')
    }

    const candidates = [this.activeTrigger, this.previousFocusedElement]

    for (const candidate of candidates) {
      if (this.isFocusRestoreTarget(candidate)) {
        return candidate
      }
    }

    if (this.activeTrigger?.closest('#menu-mobile')) {
      return document.querySelector('.menu-mobile-button')
    }

    return null
  }

  closeModal() {
    const focusTarget = this.getFocusRestoreTarget()

    this.modal.style.display = 'none'
    this.unlockScroll()
    this.setTriggersExpanded(false)
    this.activeTrigger = null
    this.openedFromMobile = false

    if (focusTarget) {
      focusTarget.focus()
    }
  }

  trapFocus() {
    const initialFocus = this.closeSpanX || this.getDialogFocusableElements()[0]

    if (initialFocus) {
      initialFocus.focus()
    }
  }

  handleFocusContainment(event) {
    if (
      !this.isOpen() ||
      !this.dialog ||
      this.dialog.contains(event.target) ||
      event.target === this.closeModalBtn
    ) {
      return
    }

    const dialogElements = this.getDialogFocusableElements()

    if (dialogElements.length > 0) {
      ;(this.closeSpanX || dialogElements[0]).focus()
    }
  }

  handleFocusTrap(event) {
    if (event.key !== 'Tab' || !this.isOpen()) {
      return
    }

    const dialogElements = Array.from(this.getDialogFocusableElements())
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length === 0) {
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    const lastDialogElement = dialogElements[dialogElements.length - 1]

    if (this.dialog && !this.dialog.contains(document.activeElement) && document.activeElement !== this.closeModalBtn) {
      event.preventDefault()
      event.shiftKey ? lastElement.focus() : firstElement.focus()
      return
    }

    if (event.shiftKey && document.activeElement === lastElement) {
      const previousElement =
        focusableElements[focusableElements.length - 2] || firstElement
      previousElement.focus()
      event.preventDefault()
    } else if (event.shiftKey && document.activeElement === firstElement) {
      lastElement.focus()
      event.preventDefault()
    } else if (
      !event.shiftKey &&
      lastDialogElement &&
      document.activeElement === lastDialogElement
    ) {
      event.preventDefault()
      if (this.closeModalBtn?.tabIndex >= 0) {
        this.closeModalBtn.focus()
      } else {
        firstElement.focus()
      }
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      firstElement.focus()
      event.preventDefault()
    }
  }
}

customElements.define('modal-currency-root', CurrencyModal)
